import { AnimatePresence, motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { CloseIcon } from '../icons'
import { analyticsService, type SessionEvent, type SessionSummary } from '../../services/analyticsService'
import { formatPlatform } from '../../utils/platform'
import { formatEventType } from '../../utils/exploreFilters'
import { crashStackTrace } from '../../utils/crash'

interface SessionTimelineDrawerProps {
  sessionId: string | null
  appId?: string
  onClose: () => void
  /** Narrow the Explore table to this session. */
  onShowInTable: (sessionId: string) => void
}

// Status colours are reserved for status and always ship with an icon + label
// (dataviz skill palette: critical #d03b3b, good #0ca30c).
const CRITICAL = '#d03b3b'
const GOOD = '#0ca30c'

function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—'
  if (seconds < 60) return `${seconds}s`
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  return `${minutes}m ${String(secs).padStart(2, '0')}s`
}

function formatOffset(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000))
  return `+${formatDuration(seconds)}`
}

function eventLabel(event: SessionEvent): string {
  if (event.event_type === 'screen_view' && event.screen_name) return event.screen_name
  if (event.event_type === 'custom' && event.event_name) return event.event_name
  return formatEventType(event.event_type)
}

export default function SessionTimelineDrawer({ sessionId, appId, onClose, onShowInTable }: SessionTimelineDrawerProps) {
  const session = useQuery({
    queryKey: ['session-timeline', appId, sessionId],
    enabled: Boolean(sessionId),
    queryFn: () => analyticsService.getSession(sessionId as string, appId),
  })

  return (
    <AnimatePresence>
      {sessionId && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-xl flex-col border-l border-white/10 bg-[#08080d]/95 shadow-2xl backdrop-blur-2xl"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 340, damping: 34 }}
            aria-label="Session timeline"
          >
            <div className="flex items-start justify-between gap-4 border-b border-white/10 p-5">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.22em] text-gray-500">Session</p>
                <h2 className="mt-2 truncate font-mono text-sm text-white" title={sessionId}>{sessionId}</h2>
                {session.data && <SessionBadges summary={session.data.summary} />}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close session timeline"
                className="rounded-xl p-2 text-gray-400 hover:bg-white/10 hover:text-white"
              >
                <CloseIcon size="md" />
              </button>
            </div>

            <div className="flex-1 space-y-5 overflow-auto p-5 custom-scrollbar">
              {session.isLoading ? (
                <div className="space-y-2" aria-hidden>
                  {[0, 1, 2, 3].map(index => <div key={index} className="h-10 animate-pulse rounded-xl bg-white/[0.04]" />)}
                </div>
              ) : session.isError ? (
                <p className="rounded-2xl border border-rose-400/20 bg-rose-500/[0.06] p-4 text-sm text-rose-200">
                  Could not load this session. It may belong to another app or have been removed.
                </p>
              ) : session.data ? (
                <>
                  <SummaryGrid summary={session.data.summary} />
                  {session.data.summary.screenPath.length > 0 && (
                    <ScreenPath path={session.data.summary.screenPath} truncated={session.data.summary.screenPathTruncated} />
                  )}
                  {session.data.truncated && (
                    <p className="rounded-xl border border-amber-400/20 bg-amber-500/[0.06] p-3 text-xs text-amber-200">
                      This session has more than {session.data.events.length.toLocaleString()} events; showing the first{' '}
                      {session.data.events.length.toLocaleString()}. Duration and counts cover those only.
                    </p>
                  )}
                  <Timeline events={session.data.events} startedAt={session.data.summary.startedAt} />
                </>
              ) : null}
            </div>

            <div className="border-t border-white/10 p-4">
              <button
                type="button"
                onClick={() => onShowInTable(sessionId)}
                className="w-full rounded-xl bg-indigo-500/80 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-500"
              >
                Show this session in the event table
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  )
}

function SessionBadges({ summary }: { summary: SessionSummary }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2 text-xs">
      <span className="rounded-full border border-white/10 px-2.5 py-1 text-gray-200">
        {formatDuration(summary.durationSeconds)} · {summary.eventCount.toLocaleString()} events
      </span>
      {summary.crashed ? (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-2.5 py-1 text-gray-200">
          <span aria-hidden style={{ color: CRITICAL }}>▲</span> Crashed
        </span>
      ) : summary.endedCleanly ? (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-2.5 py-1 text-gray-200">
          <span aria-hidden style={{ color: GOOD }}>●</span> Ended normally
        </span>
      ) : (
        <span className="rounded-full border border-white/10 px-2.5 py-1 text-gray-400" title="No session_end was recorded: the app may have been killed, or the session is still running.">
          No session end recorded
        </span>
      )}
    </div>
  )
}

function SummaryGrid({ summary }: { summary: SessionSummary }) {
  const { context } = summary
  const rows: Array<[string, string]> = [
    ['Started', summary.startedAt ? new Date(summary.startedAt).toLocaleString() : '—'],
    ['Duration', formatDuration(summary.durationSeconds)],
    ['Device', [context.platform ? formatPlatform(context.platform) : null, context.deviceModel].filter(Boolean).join(' · ') || '—'],
    ['OS', context.osVersion || '—'],
    ['App', [context.appVersion, context.buildNumber].filter(Boolean).join(' · ') || '—'],
    ['Location', [context.city, context.country].filter(Boolean).join(', ') || '—'],
  ]
  if (summary.crashReasons.length > 0) rows.push(['Crash reason', summary.crashReasons.join(' · ')])
  return (
    <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.035] p-3">
          <dt className="text-[10px] uppercase tracking-[0.18em] text-gray-600">{label}</dt>
          <dd className="mt-1 break-words text-sm font-medium text-gray-200">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function ScreenPath({ path, truncated }: { path: string[]; truncated: boolean }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-gray-200">Screen path</h3>
      <ol className="flex flex-wrap items-center gap-1.5 text-xs">
        {path.map((screen, index) => (
          <li key={`${index}-${screen}`} className="flex items-center gap-1.5">
            {index > 0 && <span aria-hidden className="text-gray-600">›</span>}
            <span className="rounded-lg border border-white/10 bg-white/[0.035] px-2 py-1 text-gray-200">{screen}</span>
          </li>
        ))}
        {truncated && <li className="text-gray-500">…</li>}
      </ol>
    </div>
  )
}

function StackTrace({ trace }: { trace: string | null }) {
  if (!trace) return null
  return (
    <details className="mt-2">
      <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-200">Stack trace</summary>
      <pre className="mt-1 max-h-[40vh] overflow-auto whitespace-pre rounded-lg border border-white/10 bg-black/40 p-3 font-mono text-[11px] leading-relaxed text-gray-300 custom-scrollbar">
        {trace}
      </pre>
    </details>
  )
}

function Timeline({ events, startedAt }: { events: SessionEvent[]; startedAt: string | null }) {
  const startMs = startedAt ? Date.parse(startedAt) : NaN
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-gray-200">Timeline</h3>
      <ol className="relative space-y-1 border-l border-white/10 pl-4">
        {events.map(event => {
          const isCrash = event.event_type === 'crash'
          const offset = Number.isFinite(startMs) ? formatOffset(Date.parse(event.occurred_at) - startMs) : ''
          return (
            <li key={event.id} className="relative">
              <span
                aria-hidden
                className="absolute -left-[1.3rem] top-3 h-2 w-2 rounded-full ring-2 ring-[#08080d]"
                style={{ background: isCrash ? CRITICAL : '#686763' }}
              />
              <div className={`rounded-xl px-3 py-2 ${isCrash ? 'border border-white/10 bg-white/[0.04]' : ''}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-sm text-gray-100">
                    {isCrash && <span className="mr-1.5" aria-hidden style={{ color: CRITICAL }}>▲</span>}
                    {eventLabel(event)}
                    {event.event_type !== 'screen_view' && event.event_type !== 'custom' ? null : (
                      <span className="ml-2 text-xs text-gray-500">{formatEventType(event.event_type)}</span>
                    )}
                  </span>
                  <span className="shrink-0 font-mono text-xs tabular-nums text-gray-500">{offset}</span>
                </div>
                {isCrash && event.crash_reason && <p className="mt-1 break-words text-xs text-gray-300">{event.crash_reason}</p>}
                {isCrash && <StackTrace trace={crashStackTrace(event)} />}
                {event.occurred_at_adjusted && (
                  <p className="mt-1 text-xs text-gray-500">
                    Placed at the crash time. Reported on the next launch, {new Date(event.timestamp).toLocaleString()}.
                  </p>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
